import { Envs, Utils } from '@common';
import { React, Styled, Styled as styled } from '@common/3rd';
import { DeskComponentRegistry } from '@component';
import {
	CheckableWidgetState, StyledPropsTheme, DeskTextComponents,
	DeskTextProps, CodeItem
} from '@my-types';
import { DeskText } from './desk-text';

export type CodeItems = CodeItem & {
	children?: CodeItems[];
}


class DeskMultipleSelect<P extends DeskTextProps & {
	clearable?: boolean;
	codes: CodeItems | CodeItems[];
	showAll: boolean;
},
	S extends CheckableWidgetState & {
		showPicker: boolean;
		peddingValue: CodeItem[];
	},
	C extends DeskTextComponents & { DisplayLabel: any }> extends DeskText<P, S, C> {
	labelRef: React.RefObject<any>;

	constructor(props: any, context?: any) {
		super(props, context);
		this.labelRef = React.createRef();
	}

	componentDidMount() {
		super.componentDidMount();
	}


	protected initComponents(): C {
		const C = super.initComponents();
		C.Box = Styled(C.Box).attrs({
			'data-widget': 'desk-multiple-select'
		})`
			position: relative;
			width: 100%;
			display: flex;
			text-align: left;
			`;
		C.Text = Styled(C.Text)`
			opacity:0;
			padding: 10px 0;
			display: inline-block;
			cursor: pointer;
			border: none;
			width: 100%;
			text-align: left;
			background-color: transparent;
			&::before {
				content: ' ';
				position: absolute;
				z-index: 1;
				height: 20px;
				top: -7px;
				right: 13px;
				border-left: 3px solid transparent;
				border-right: 3px solid transparent;
				border-bottom: 4px solid #a7a8aa;
			}
			&::after {
				content: ' ';
				position: absolute;
				z-index: 1;
				top: 20px;
				right: 13px;
				border-top: 4px solid #a7a8aa;
				border-left: 3px solid transparent;
				border-right: 3px solid transparent;
			}
		`;
		C.DisplayLabel = Styled.span`
			display: block;
			position: absolute;
			height: ${props => props.theme.INPUT_HEIGHT}px;
			pointer-events: none;
			${props => {
				const cText = document.getElementsByClassName(C.Text.styledComponentId);
				// console.log(cText);
				if (cText.length > 0) {
					const elem = cText[0] as Element;
					//@ts-ignore
					const elemWidth = elem.offsetWidth;
					// console.log(elemWidth);
					return `width:${elemWidth}px;`
				} else {
					return `width:100%;`
				}
			}}
			overflow:hidden;
		　　 text-overflow:ellipsis;
		　　 white-space:nowrap;
			/* 获取焦点的时候, 如果有错误信息, 显示 */
			+ span[data-widget=text-reminder] {
				display: block;
				position: absolute;
				top: 30px;
				text-align: right;
				color: ${props => props.theme.COLOR_ERROR};
				transform: scale(0.9);
				transform-origin: right;
				white-space: nowrap;
				width: 180%;
				right: 0;
			}
		`;
		return C;
	}

	protected renderDisplayText(): JSX.Element {
		const C = this.getComponents();
		// 没有数据的情况下模拟一个假的placeholder
		return <C.DisplayLabel ref={this.labelRef}>
			{this.formatValue(this.getValueFromModel())}
		</C.DisplayLabel>;
	}

	isClearable(): boolean {
		return Utils.isUndefined(this.props.clearable) ? true : (this.props.clearable as boolean);
	}
	getCodes(): CodeItems[] {
		if (this.isShowAll() && (Utils.toArray(this.props.codes) || []).length > 0) {
			return Utils.toArray({
				v: '-1',
				l: '全部',
				children: Utils.toArray(this.props.codes) || []
			}
			) || [];
		} else {
			return Utils.toArray(this.props.codes) || [];
		}
	}

	render() {
		const C = this.getComponents();
		let input = <C.Text
			onClick={this.onMultipleSelectClicked}
			ref={this.textRef}
			readOnly={true}
			defaultValue={this.asDOMAccepted(this.getValueFromModel())}
		/>;
		let selectPicker =
			<MultipleSelectBoxPicker show={this.isMultipleSelectBoxPickerShown()}
				value={this.asDOMAccepted(this.getValueFromModel())}
				pedValue={this.initPeddingValue()}
				selectOptions={this.getCodes()}
				flatCodesFunc={(items: CodeItems[]) => { return this.flatCodes(items) }}
				flatSelectOptions={this.flatCodes(this.getCodes())}
				hide={this.hideMultipleSelectBoxPicker}
				confirm={this.confirmMultipleSelectBoxPickerValue}
			/>;
		//iphone 只读readonly,弹框，
		if (Envs.isIphone() && !this.isEditable()) {
			input = <C.Text
				onClick={this.onMultipleSelectClicked}
				ref={this.textRef}
				readOnly={!this.isEditable()}
				disabled={true}
				defaultValue={this.asDOMAccepted(this.getValueFromModel())}
			/>;
		}
		return (
			<C.Box>
				{input}
				{this.renderDisplayText()}
				{selectPicker}
				{this.renderReminder()}
			</C.Box>
		);
	}

	protected paintColor(theme: StyledPropsTheme): string {
		const css = super.paintColor(theme);

		if (this.isCheckFailOnWhatever()) {
			return `${css} + span {color: ${theme.COLOR_ERROR};}`;
		} else {
			return css;
		}
	}

	protected asDOMAccepted(value?: string | null): string {
		// HTML 标准要求必须是横杠(-), 参考RFC3339
		return value ? value : '';
	}

	protected initPeddingValue(): CodeItem[] {
		const cValue = this.asDOMAccepted(this.getValueFromModel());
		if (cValue && !Utils.isBlank(cValue)) {
			const pValue = this.flatCodes(this.getCodes()).filter(item => cValue ? cValue!.split(',').includes(String(item.v)) : false);
			return pValue;
		}
		return [];
	}

	//取最底层的codes集合
	protected flatCodes(items: CodeItems[]): CodeItem[] {
		return (items || []).reduce((array, item: CodeItems) => {
			let children = item.children || [] as CodeItems[];
			if (children && children.length > 0) {
				//@ts-ignore
				array = array.concat(this.flatCodes(children))
			} else {
				//@ts-ignore
				array.push(item);
			}
			return array;
		}, []);
	}

	protected isShowAll(): boolean {
		return this.props.showAll === true;
	}


	protected formatValue(value?: string | null, format?: string): string {
		let displayValue = '';
		if (value != null && !Utils.isEmpty(value)) {
			const codes = this.flatCodes(this.getCodes());
			displayValue = codes.map((item: CodeItem) => {
				if (value.split(',').includes(String(item.v))) {
					return item.l;
				}
				return '';
			}).filter(s => !Utils.isBlank(s)).join(',');
		}
		return displayValue;
	}

	protected syncValueToDOMFromModel(): void {
		this.textRef.current.value = this.asDOMAccepted(this.getValueFromModel());
	}

	protected setValueToDOM(value: any, propName?: string): this {
		if (propName == null || propName === this.getPropName()) {
			this.textRef.current.value = this.asDOMAccepted(value);
			// this.checkData();
			this.forceUpdate();
		}
		return this;
	}

	protected getValueFromModel(propName?: string): any {
		const value = super.getValueFromModel(propName);
		if (!propName || propName === this.getPropName()) {
			return value;
		} else {
			return value;
		}
	}

	protected setValueToModel(value: any, propName?: string): this {
		if (!propName || propName === this.getPropName()) {
			return super.setValueToModel(value, propName);
		} else {
			return super.setValueToModel(value, propName);
		}
	}


	private onMultipleSelectClicked = () => {
		if (this.state.showPicker) {
			this.hideMultipleSelectBoxPicker();
		} else {
			this.showMultipleSelectBoxPicker();
		}
	}

	private isMultipleSelectBoxPickerShown() {
		return this.state.showPicker ?? false;
	}

	private hideMultipleSelectBoxPicker = () => {
		Envs.application()!.setScrollDisabled(false);
		this.setState({
			showPicker: false,
		});
	};

	private showMultipleSelectBoxPicker = () => {
		if (!this.isEditable()) {
			return;
		}
		Envs.application()!.setScrollDisabled(true);
		this.setState({
			showPicker: true,
			peddingValue: this.initPeddingValue()
		});
	};

	private confirmMultipleSelectBoxPickerValue = async (value: string | null) => {
		const oldValue = this.getValueFromModel();
		if (value === oldValue) {
			// 没有变化, 什么都不做
			return;
		}

		await this.setValueToModel(value).checkData();
	};
}

const MultipleSelectBox = styled.div`
	li {
		list-style: none;
	}

	ul li {
		list-style: none;
		padding: 0;
	}

	&[data-show=false] {
		pointer-events: none;
		display:none;
	}

	position: absolute;
    margin-top: 4px;
    padding: 0 10px;
    height: 325px;
	width:100%;
	right:0;
    border-radius: 4px;
    background-color: #fff;
    z-index: 4;
	box-shadow: 0 0 2px rgba(0, 0, 0, .3);

	.react-multi-select-panel{
		display: flex;
		height: 285px;

		.react-multi-select-col {
			min-width: 200px;
			overflow-y: auto;
			.selected-option-row {
				padding: 5px 0;
				cursor: pointer;
			}

			.react-multiple-select-type-name {
				padding: 5px 0 4px 0;
				border-bottom: 1px solid #eee;
				color: #c5c5c5;
			}

			.react-multi-select-list-option{
						
				&:last-child {
					padding-bottom: 0;
				}
				.eg-input.checkbox {
					.input-icon {
						margin-right: 2px;
					}
				}
				.eg-input.checkbox.active {
					.input-icon {
						fill:  ${props => props.theme.COLOR_PRIMARY};
					}
				}

				.react-multi-select-box-option{
					position: relative;
					padding: 5px 0 5px 20px;
					margin: 0;
					cursor: pointer;
					display: block;
					text-decoration: none;
					&:before {
						content: ' ';
						position: absolute;
						text-align: center;
						line-height: 1.1;
						left: 2px;
						top: 15px;
						height: 14px;
						width: 14px;
						margin-right: 10px;
						border: 1px solid #7B8E9B;
						background: #f9f9f9;
						vertical-align: middle;
					}
				}

				.react-multi-select-box-option-selected{
					&:before {
						content: '\\2713';
						color:  ${props => props.theme.COLOR_PRIMARY};
					}
				}

				
			}

			.react-multi-select-classification {
				.classification {
					padding: 5px 0;
					cursor: pointer;

					.react-multi-select-box-option{
						position: relative;
						padding: 5px 0 5px 20px;
						margin: 0;
						cursor: pointer;
						display: inline;
						text-decoration: none;
						&:before {
							content: ' ';
							position: absolute;
							text-align: center;
							line-height: 1.1;
							left: 2px;
							top: 9px;
							height: 14px;
							width: 14px;
							margin-right: 10px;
							border: 1px solid #7B8E9B;
							background: #f9f9f9;
							vertical-align: middle;
						}
					}
	
					.react-multi-select-box-option-selected{
						&:before {
							content: '\\2713';
							color:  ${props => props.theme.COLOR_PRIMARY};
						}
					}
	

					.react-multi-select-list-arrow {
						position: relative;
						display: inline-block;
						vertical-align: middle;
						width: 8px;
						height: 8px;
						&::before {
							content: ' ';
							position: absolute;
							border-top: 3px solid transparent;
							border-bottom: 3px solid transparent;
							border-left:  4px solid ${props => props.theme.COLOR_PRIMARY};
						}
					}
					
					.react-multi-select-list-arrow.expand {
						&::before {
							content: ' ';
							position: absolute;
							border-left: 3px solid transparent;
							border-right: 3px solid transparent;
							border-top: 4px solid ${props => props.theme.COLOR_PRIMARY};
						}
					}
				}

				.react-multi-select-sub-options {
					padding-left: 16px;

					.react-multi-select-list-option{
						
						&:last-child {
							padding-bottom: 0;
						}
						.eg-input.checkbox {
							.input-icon {
								margin-right: 2px;
							}
						}
						.eg-input.checkbox.active {
							.input-icon {
								fill:  ${props => props.theme.COLOR_PRIMARY};
							}
						}
	
						.react-multi-select-box-option{
							position: relative;
							padding: 5px 0 5px 20px;
							margin: 0;
							cursor: pointer;
							display: block;
							text-decoration: none;
							&:before {
								content: ' ';
								position: absolute;
								text-align: center;
								line-height: 1.1;
								left: 2px;
								top: 15px;
								height: 14px;
								width: 14px;
								margin-right: 10px;
								border: 1px solid #7B8E9B;
								background: #f9f9f9;
								vertical-align: middle;
							}
						}
		
						.react-multi-select-box-option-selected{
							&:before {
								content: '\\2713';
								color:  ${props => props.theme.COLOR_PRIMARY};
							}
						}
	
						
					}
					
				}

				.react-multi-select-hide {
					display: none;
				}
			}
		}

		.react-multi-select-sign {
			display: flex;
			flex-direction: column;
			justify-content: center;
			width: 15px;
			i {
				width: 6px;
				height: 6px;
				border-top: 2px solid #a9a9a9;
				border-right: 2px solid #a9a9a9;
				-webkit-transform: rotate(45deg);
				-moz-transform: rotate(45deg);
				-o-transform: rotate(45deg);
				transform: rotate(45deg);
			}
		}

		

	}

	.react-multi-select-area-btn {
		padding: 5px 0;
		height: 40px;
		border-top: 1px solid #eee;
		text-align: center;
		.eg-btn {
			padding: 4px 20px;
			margin-right: 10px;
			&:last-child {
				margin-right: 0;
			}
		}

		.react-multi-select-btn {
			padding: 4px 20px;
			border-radius: 6px;
			background-color: ${props => props.theme.COLOR_PRIMARY};
			display: inline-block;
			margin-right: 10px;
			margin-bottom: 0;
			text-align: center;
			vertical-align: middle;
			touch-action: manipulation;
			cursor: pointer;
			background-image: none;
			border: 1px solid transparent;
			white-space: nowrap;
			outline: 0;
			text-decoration: none;
			font-weight: normal;
			font-size: 14px;
			line-height: 1.42857143;
			-webkit-user-select: none;
			-moz-user-select: none;
			-ms-user-select: none;
			user-select: none;
			color: #fff;
			border-color: transparent;
			&:last-child {
				margin-right: 0;
			}
		}
		
		.react-multi-select-btn-white {
			background-color: #fff;
			color: #333;
			border-color: #d5d5d5;
		}
	}
`;



const MultipleSelectBoxPicker = (props: {
	readonly value?: string,
	pedValue: CodeItem[],
	show: boolean,
	confirm: (value: string | null) => void,
	flatCodesFunc: (item: CodeItems[]) => CodeItem[];
	hide: () => void,
	selectOptions: CodeItems[];
	flatSelectOptions: CodeItem[];
}) => {
	const {
		value, show, hide, confirm, selectOptions = [], flatSelectOptions = [], pedValue = [], flatCodesFunc
	} = props;

	const [, forceUpdate] = React.useReducer(x => x + 1, 0);

	const initPeddingValue = (cValue: string | undefined): CodeItem[] => {
		if (cValue && !Utils.isBlank(cValue)) {
			const pValue = flatSelectOptions.filter(item => cValue ? cValue!.split(',').includes(String(item.v)) : false);
			return pValue;
		}
		return [];
	}
	const [peddingValue, setPeddingValue] = React.useState<Array<CodeItem>>(pedValue);

	const toggleClass = (fullClassName: string, className: string): string => {
		return fullClassName.includes(className) ? fullClassName.replace(className, '') : fullClassName.trim() + ' ' + className;
	};

	//取最底层的codes集合
	const flatCodes = (items: CodeItems[]): CodeItem[] => {
		return (items || []).reduce((array, item: CodeItems) => {
			let children = item.children || [] as CodeItems[];
			if (children && children.length > 0) {
				//@ts-ignore
				array = array.concat(flatCodes(children))
			} else {
				//@ts-ignore
				array.push(item);
			}
			return array;
		}, []);
	}

	const handleConfirm = (): void => {
		const pValue = peddingValue;
		const value = pValue.map((item: CodeItem) => item.v).join(',');
		confirm(value);
		setPeddingValue(initPeddingValue(value));
		hide();
	}

	const handleCancel = (): void => {
		setPeddingValue(initPeddingValue(value));
		hide();
	}

	const isInclude = (peddingValue: CodeItem[], item: CodeItems): boolean => {
		const containCodes = flatCodes([item]);
		let isInclude = peddingValue.some((p: CodeItem) => containCodes.includes(p));
		return isInclude;
	}

	const isIncludeLeaf = (peddingValue: CodeItem[], item: CodeItem): boolean => {
		// eslint-disable-next-line
		let isInclude = peddingValue.some((p: CodeItems) => p.v == item.v);
		return isInclude;
	}


	const handleToggleOption = (event: any): void => {
		let iconDOM = event.currentTarget.firstChild;
		let ulDOM = event.currentTarget.parentNode.lastChild;
		iconDOM.setAttribute('class', toggleClass(iconDOM.getAttribute('class'), 'expand'));
		ulDOM.setAttribute('class', toggleClass(ulDOM.getAttribute('class'), 'react-multi-select-hide'));
	}

	const classficationClicked = (item: any, isLeafParent: boolean, evt: any): void => {
		evt.preventDefault();
		evt.stopPropagation();
		//双击全选或者全部清除当前节点的子节点
		const children = item.children || [];
		//判断children中的元素是不是页节点,没有children集合了，表示是页节点
		const codes = flatCodesFunc(children);
		//判断是否有选中的，有就清除，没有的话全部选中
		if (isLeafParent) {
			// eslint-disable-next-line
			const hasSelectValue = codes.some((cItem: CodeItems) => peddingValue.includes(cItem)) == true;
			if (hasSelectValue) {
				const pValue = peddingValue.filter((cItem: CodeItems) => codes.indexOf(cItem) === -1);
				setPeddingValue(pValue);
			} else {
				const pValue = peddingValue.concat(children);
				setPeddingValue(pValue);
			}
		} else {
			// eslint-disable-next-line
			const hasSelectValue = codes.some((cItem: CodeItems) => peddingValue.includes(cItem)) == true;
			if (hasSelectValue) {
				const pValue = peddingValue.filter((cItem: CodeItems) => codes.indexOf(cItem) === -1);
				setPeddingValue(pValue);
			} else {
				const pValue = peddingValue.concat(codes);
				setPeddingValue(pValue);
			}
		}

	}


	const renderSelectOptBtn = (): JSX.Element | void => {
		return (
			<div className="react-multi-select-area-btn">
				<button className="react-multi-select-btn" onClick={handleConfirm}>确认</button>
				<button className="react-multi-select-btn react-multi-select-btn-white" onClick={handleCancel}>取消</button>
			</div>
		)
	}

	const renderSelectOption = (): JSX.Element | void => {
		return (
			<ul className="react-multi-select-col">
				{
					selectOptions.map((item: any, index: number) => {
						return renderOption(item, index + '', '');
					})
				}
			</ul>
		)
	}


	const renderOption = (item: any, index: string, key: string): JSX.Element | void => {
		if (item.hasOwnProperty('children') && (item.children || []).length > 0) {
			return renderClassification(item, index, key);
		} else {
			return renderRow(item);
		}
	}

	const renderClassification = (item: any, index: string, key: string): JSX.Element | void => {
		let loaded = ((item.hasOwnProperty('children') && item.children.length > 0)) ? true : false;
		if (key === '') {
			key = item.v;
		} else {
			key += '-' + item.v;
		}
		const isLeafParent = (item.children || []).every((item: CodeItems) => !item.children) === true;
		const itemCodes = flatCodes(item.children || []);
		let selectNum = peddingValue.filter((cItem: CodeItem) => itemCodes.includes(cItem)).length;

		let className = 'react-multi-select-box-option';
		if (isInclude(peddingValue, item)) {
			className += ' react-multi-select-box-option-selected';
		}

		return (
			<li key={key} className="react-multi-select-classification">
				<div className="classification" onClick={handleToggleOption} >

					<i className="react-multi-select-list-arrow" />
					{
						<span><span className={className} onClick={(event: any) => classficationClicked(item, isLeafParent, event)} /><span>{item.l + '(' + selectNum + '/' + itemCodes.length + ')'}</span></span>
					}
				</div>
				<ul key={`sub${item.v}`} className="react-multi-select-sub-options react-multi-select-hide">
					{
						loaded && item.children.map((sub: any, i: number) => {
							return renderOption(sub, index + '-' + i, key);
						})
					}
				</ul>
			</li>
		)
	}

	const renderRow = (item: any): JSX.Element | void => {
		let className = 'react-multi-select-box-option';
		if (isIncludeLeaf(peddingValue, item)) {
			className += ' react-multi-select-box-option-selected';
		}
		return (
			<li key={item.v} className="react-multi-select-list-option">
				<div className={className}
					//@ts-ignore
					value={item.v} label={item.l} onClick={(): void => {
						const pValue = peddingValue;
						if (!isInclude(pValue, item)) {
							//加入
							pValue.push(item);
							setPeddingValue(pValue);
							forceUpdate();
						} else {
							//移除
							var index = pValue.indexOf(item);
							if (index > -1) {
								pValue.splice(index, 1);
							}
							setPeddingValue(pValue);
							forceUpdate();
						}
					}
					}>{item.l}</div>
			</li>
		)
	}



	const renderSelectedOption = (item: any, index: number): JSX.Element | void => {
		return (
			<li key={item.v} className="selected-option-row"
				//@ts-ignore
				onClick={(): void => {
					const pValue = peddingValue;
					var index = pValue.indexOf(item);
					if (index > -1) {
						pValue.splice(index, 1);
					}
					setPeddingValue(pValue);
					forceUpdate();
				}}>{item.l}</li>
		)
	}

	const renderSelectedValue = (): JSX.Element | void => {
		return (
			<div className="react-multi-select-col">
				{/* <div className="react-multiple-select-type-name">{'selected'}：</div> */}
				<ul>
					{
						peddingValue.map((item: any, index: number) => {
							return renderSelectedOption(item, index);
						})
					}
				</ul>
			</div>
		)
	}


	React.useEffect(() => {
		(async () => {
			setPeddingValue(pedValue);
		})();
		return () => {
		};
	}, [pedValue]);


	return <MultipleSelectBox data-show={show}>
		<div className="react-multi-select-panel" tab-index="-1">
			{renderSelectOption()}
			<div className="react-multi-select-sign">
				<i />
			</div>
			{renderSelectedValue()}
		</div>
		{renderSelectOptBtn()}
	</MultipleSelectBox>;
};

DeskComponentRegistry.register({
	multipleSelect: DeskMultipleSelect
});

export { DeskMultipleSelect };
